// hooks/use-i18n.js — INTERNATIONALIZATION HOOK
// ═══════════════════════════════════════════════════════════
// Single source for all UI strings.
// Usage: const { t } = useI18n();
//        t('nav.space') → 'Space'
//        t('common.save') → 'Save'
// Falls back to English if key missing in current language.
// Language stored in localStorage + user preferences.
// ═══════════════════════════════════════════════════════════
import { useState, useEffect, useCallback, createContext, useContext } from 'react';

// ── Language files ──
import { strings as en } from '../config/i18n/en';

// Add new languages here as they're created:
// import { strings as es } from '../config/i18n/es';
// import { strings as zh } from '../config/i18n/zh';

const LANGUAGES = {
  en: { label: 'English', strings: en },
  // es: { label: 'Español', strings: es },
  // zh: { label: '中文', strings: zh },
  // ja: { label: '日本語', strings: ja },
  // ko: { label: '한국어', strings: ko },
  // fr: { label: 'Français', strings: fr },
  // pt: { label: 'Português', strings: pt },
  // vi: { label: 'Tiếng Việt', strings: vi },
  // th: { label: 'ไทย', strings: th },
};

export const AVAILABLE_LANGUAGES = Object.entries(LANGUAGES).map(([code, { label }]) => ({ code, label }));

// ── Context ──
const I18nContext = createContext(null);

/**
 * Resolve a nested key like 'nav.space' from an object.
 */
function resolve(obj, path) {
  return path.split('.').reduce((o, k) => o?.[k], obj);
}

/**
 * I18n Provider — wrap the app with this.
 * Provides t() function and language state to all children.
 */
export function I18nProvider({ children, initialLang }) {
  const [lang, setLang] = useState(() => {
    if (typeof window === 'undefined') return initialLang || 'en';
    return localStorage.getItem('shift_language') || initialLang || 'en';
  });

  const setLanguage = useCallback((code) => {
    if (LANGUAGES[code]) {
      setLang(code);
      localStorage.setItem('shift_language', code);
    }
  }, []);

  const t = useCallback((key, fallback) => {
    const current = LANGUAGES[lang]?.strings;
    const result = resolve(current, key);
    if (result !== undefined) return result;
    // Fallback to English
    if (lang !== 'en') {
      const enResult = resolve(en, key);
      if (enResult !== undefined) return enResult;
    }
    // Fallback to provided default or the key itself
    return fallback || key;
  }, [lang]);

  return (
    <I18nContext.Provider value={{ t, lang, setLanguage, languages: AVAILABLE_LANGUAGES }}>
      {children}
    </I18nContext.Provider>
  );
}

/**
 * Hook to access i18n in any component.
 * Returns: { t, lang, setLanguage, languages }
 */
export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Fallback for components not wrapped in provider — use English directly
    return {
      t: (key, fallback) => resolve(en, key) || fallback || key,
      lang: 'en',
      setLanguage: () => {},
      languages: AVAILABLE_LANGUAGES,
    };
  }
  return ctx;
}
